// client/src/pages/POA/POANewPage.tsx
// Multi-step Form 2848 POA creation wizard.
// Step 0: client search modal → Steps 1-5: form

import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { usePOAForm } from './usePOAForm';
import { STEP_VALIDATORS } from './poaValidation';
import type { StepErrors, POAPrefillData, POAFormState, POAPrefillIndividual } from '../../types/poa';
import ClientSearchModal,    { type PEClient } from './ClientSearchModal';
import PrefillLoadingModal                    from './PrefillLoadingModal';
import AIAssistModal                          from './AIAssistModal';
import TaxpayerStep        from './steps/TaxpayerStep';
import RepresentativesStep from './steps/RepresentativesStep';
import TaxMattersStep      from './steps/TaxMattersStep';
import ActsStep            from './steps/ActsStep';
import ReviewStep          from './steps/ReviewStep';
import { createPOA, getPOAPrefill } from '../../services/poa';

const STEPS = [
  'Taxpayer Info',
  'Representatives',
  'Tax Matters',
  'Acts & Limits',
  'Review & Submit',
];

export default function POANewPage() {
  const navigate = useNavigate();
  const [step,       setStep]       = useState(0);
  const [errors,     setErrors]     = useState<StepErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [client,     setClient]     = useState<PEClient | null>(null); // null = modal visible

  // Prefill state
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillData,    setPrefillData]    = useState<POAPrefillData | null>(null);
  const prefillSkipped = useRef(false);
  // For individual returns with a spouse, user must choose before the form starts
  const [personChosen,   setPersonChosen]   = useState(false);
  // Which of the two this pass covers — drives who the follow-up POA is for.
  const [chosenPerson,   setChosenPerson]   = useState<'taxpayer' | 'spouse'>('taxpayer');

  // Spouse copy feature — after creating the first POA, auto-prepare the spouse's
  const [copyToSpouse,   setCopyToSpouse]   = useState(false);
  const [isSpouseCopy,   setIsSpouseCopy]   = useState(false); // true when we're on the spouse pass

  // AI Assist modal
  const [aiModalOpen, setAiModalOpen] = useState(false);

  const {
    form,
    setTaxpayerField,
    applyPrefill,
    applyFormState,
    addRep, removeRep, updateRep,
    addMatter, removeMatter, updateMatter, addCivilPenaltyMatter,
    clearDraft,
    resetForSpouse,
  } = usePOAForm();

  // ── Client selection + CCH prefill ──────────────────────────────────────
  async function handleClientSelect(c: PEClient) {
    setClient(c);
    setTaxpayerField('peClientCode', c.clientCode);
    setTaxpayerField('taxpayerName', c.clientName); // fallback name while prefill loads
    setPrefillData(null);
    setPersonChosen(false);
    setChosenPerson('taxpayer');
    setIsSpouseCopy(false);
    setCopyToSpouse(false);
    prefillSkipped.current = false;
    setPrefillLoading(true);
    try {
      const result = await getPOAPrefill(c.clientCode);
      if (prefillSkipped.current) return; // user skipped — ignore result
      if (result.prefill) {
        setPrefillData(result.prefill);
        // Non-individual or single filer — apply immediately, no chooser needed
        const needsChoice = result.prefill.type === 'individual' && result.prefill.spouse !== null;
        if (!needsChoice) {
          applyPrefill(result.prefill);
          setPersonChosen(true);
        }
        if (result.warning) toast.info(result.warning);
      } else {
        if (result.warning) toast.info(result.warning);
        setPersonChosen(true); // no data — let user proceed manually
      }
    } catch (err: any) {
      if (prefillSkipped.current) return;
      toast.info('No CCH return data found. Please fill in taxpayer details manually.');
      setPersonChosen(true);
    } finally {
      if (!prefillSkipped.current) setPrefillLoading(false);
    }
  }

  function handleSkipPrefill() {
    prefillSkipped.current = true;
    setPrefillLoading(false);
    setPersonChosen(true);
  }

  function handlePersonChoice(key: 'taxpayer' | 'spouse') {
    if (prefillData) applyPrefill(prefillData, key);
    setChosenPerson(key);
    setPersonChosen(true);
  }

  // ── AI Assist — form payload received from AI ──────────────────────────
  function handleAiFormReady(payload: Record<string, unknown>) {
    setAiModalOpen(false);
    // Apply the AI-generated form state
    applyFormState(payload as Partial<POAFormState>);
    // Set the client pill from the payload
    const code = (payload.peClientCode as string) || '';
    const name = (payload.taxpayerName as string) || '';
    setClient({ clientCode: code, clientName: name, contIndex: null, partnerName: '', managerName: '', officeTitle: '' });
    setPersonChosen(true);
    setStep(0); // land on Step 1 (Taxpayer Info) for review
    setErrors({});
    toast.success('AI prepared your Form 2848 — review and submit when ready.');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Step navigation ─────────────────────────────────────────────────────
  function validate(stepIndex: number): boolean {
    if (stepIndex >= STEP_VALIDATORS.length) return true;
    const errs = STEP_VALIDATORS[stepIndex](form);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleNext() {
    if (!validate(step)) return;
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function handleBack() {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // ── Submit ───────────────────────────────────────────────────────────────
  async function handleSubmit() {
    setSubmitting(true);
    try {
      const result = await createPOA(form);

      // If "copy to spouse" is checked and we have spouse prefill data, reset for spouse
      const shouldCopyToSpouse =
        copyToSpouse &&
        !isSpouseCopy &&
        prefillData?.type === 'individual' &&
        prefillData.spouse != null;

      if (shouldCopyToSpouse) {
        const otherKey  = chosenPerson === 'spouse' ? 'taxpayer' : 'spouse';
        const otherName = otherKey === 'spouse'
          ? (prefillData as POAPrefillIndividual).spouse!.name
          : (prefillData as POAPrefillIndividual).taxpayer.name;
        toast.success(`POA created! Now preparing Form 2848 for ${otherName}...`);
        resetForSpouse();
        applyPrefill(prefillData!, otherKey, true);
        setChosenPerson(otherKey);
        setCopyToSpouse(false);
        setIsSpouseCopy(true);
        setPersonChosen(true);
        setStep(0);
        setErrors({});
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        clearDraft();
        toast.success('POA record created successfully.');
        navigate(`/poa/${result.id}`);
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create POA. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const isLastStep       = step === STEPS.length - 1;

  // The joint filer this pass does NOT cover — the one a follow-up POA would be for.
  const jointPartner =
    prefillData?.type === 'individual' && prefillData.spouse
      ? (chosenPerson === 'spouse' ? prefillData.taxpayer : prefillData.spouse)
      : null;

  // Offer the follow-up POA on the review step of the first pass only.
  const canOfferPartnerCopy = !isSpouseCopy && jointPartner != null;

  // The spouse rarely has an email of their own in PE, so their form inherits the
  // taxpayer's. Only flag it when that is actually what happened.
  const usingTaxpayerEmailForSpouse =
    isSpouseCopy && chosenPerson === 'spouse'
    && prefillData?.type === 'individual' && !prefillData.spouse?.email
    && !!form.taxpayerEmail;
  const awaitingChoice   = !prefillLoading && prefillData?.type === 'individual' && !!prefillData.spouse && !personChosen;

  return (
    <>
      {/* Step 0: client search modal — blocks until a client is selected */}
      {!client && !aiModalOpen && (
        <ClientSearchModal
          onSelect={handleClientSelect}
          onAiAssist={() => setAiModalOpen(true)}
        />
      )}

      {/* AI Assist modal */}
      {aiModalOpen && (
        <AIAssistModal
          onFormReady={handleAiFormReady}
          onClose={() => setAiModalOpen(false)}
        />
      )}

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
        {/* Header — shows selected client once chosen */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>New Power of Attorney</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Form 2848 (Rev. January 2021)</div>
          {client && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              marginTop: 10, padding: '6px 12px', borderRadius: 99,
              background: 'rgba(122,162,255,.1)', border: '1px solid rgba(122,162,255,.25)',
              fontSize: 13,
            }}>
              <span style={{ color: 'var(--muted)' }}>Client:</span>
              <span style={{ fontWeight: 600 }}>{client.clientName}</span>
              <span style={{ color: 'var(--muted)' }}>{client.clientCode}</span>
              <button
                onClick={() => {
                  setClient(null); setStep(0); setPrefillData(null); setPersonChosen(false);
                  setChosenPerson('taxpayer'); setIsSpouseCopy(false); setCopyToSpouse(false);
                }}
                style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px' }}
                title="Change client"
              >×</button>
            </div>
          )}
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border)' }}>
          {STEPS.map((label, i) => {
            const isActive = i === step;
            const isDone   = i < step;
            return (
              <div
                key={i}
                style={{
                  flex: 1, padding: '10px 6px', textAlign: 'center', fontSize: 12, fontWeight: isActive ? 700 : 400,
                  borderRight: i < STEPS.length - 1 ? '1px solid var(--border)' : 'none',
                  background: isActive ? 'rgba(122,162,255,.15)' : isDone ? 'rgba(87,211,140,.08)' : 'rgba(255,255,255,.02)',
                  color: isActive ? 'var(--accent)' : isDone ? 'var(--good)' : 'var(--muted)',
                  transition: 'background .2s',
                }}
              >
                <div style={{ fontSize: 16, marginBottom: 2 }}>{isDone ? '✓' : i + 1}</div>
                {label}
              </div>
            );
          })}
        </div>

        {/* CCH prefill loading modal */}
        {prefillLoading && client && (
          <PrefillLoadingModal
            clientName={client.clientName}
            onSkip={handleSkipPrefill}
          />
        )}

        {/* Individual TP/Spouse chooser */}
        {!prefillLoading && prefillData?.type === 'individual' && prefillData.spouse && !personChosen && (
          <div style={{ marginBottom: 20, padding: '18px 20px', borderRadius: 10, background: 'rgba(122,162,255,.07)', border: '1px solid rgba(122,162,255,.2)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Who is this POA for?</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
              This is a joint return. Select which individual this Form 2848 covers.
              A separate POA is required for each taxpayer.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className="btn"
                onClick={() => handlePersonChoice('taxpayer')}
                style={{ flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: 8 }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{prefillData.taxpayer.name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  SSN: {prefillData.taxpayer.ssn.replace(/^\d{3}-\d{2}/, 'XXX-XX')}
                </div>
              </button>
              <button
                className="btn"
                onClick={() => handlePersonChoice('spouse')}
                style={{ flex: 1, textAlign: 'left', padding: '12px 14px', borderRadius: 8 }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{prefillData.spouse.name}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  SSN: {prefillData.spouse.ssn.replace(/^\d{3}-\d{2}/, 'XXX-XX')}
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Spouse copy banner — shown when on the auto-generated spouse pass */}
        {isSpouseCopy && (
          <div style={{
            marginBottom: 16, padding: '10px 16px', borderRadius: 8, fontSize: 13,
            background: 'rgba(158,206,106,.08)', border: '1px solid rgba(158,206,106,.25)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontWeight: 600, color: 'var(--ok)' }}>Second POA</span>
            <span style={{ color: 'var(--muted)' }}>
              Now covering <strong style={{ color: 'var(--text)' }}>{form.taxpayerName}</strong>.
              Review the details, then page through to create.
            </span>
          </div>
        )}

        {/* The spouse had no email of their own in PE, so this form inherited the
            taxpayer's. Say so plainly rather than letting it surface at send time. */}
        {isSpouseCopy && usingTaxpayerEmailForSpouse && (
          <div style={{
            marginBottom: 16, padding: '10px 16px', borderRadius: 8, fontSize: 13,
            background: 'rgba(255,209,102,.07)', border: '1px solid rgba(255,209,102,.25)',
            lineHeight: 1.5,
          }}>
            <span style={{ fontWeight: 600, color: 'var(--warn)' }}>Heads up — </span>
            <span style={{ color: 'var(--muted)' }}>
              Practice Engine has no separate email for the spouse, so this POA will go to{' '}
              <strong style={{ color: 'var(--text)' }}>{form.taxpayerEmail}</strong> for signature.
              Change it on the Taxpayer step if the spouse should receive their own copy.
            </span>
          </div>
        )}

        {/* Step content */}
        <div style={{ minHeight: 300 }}>
          {step === 0 && (
            <TaxpayerStep form={form} errors={errors} onChange={setTaxpayerField} />
          )}
          {step === 1 && (
            <RepresentativesStep
              form={form} errors={errors}
              onAdd={addRep} onRemove={removeRep} onUpdate={updateRep}
            />
          )}
          {step === 2 && (
            <TaxMattersStep
              form={form} errors={errors}
              onAdd={addMatter} onRemove={removeMatter} onUpdate={updateMatter}
              onAddCivilPenalty={addCivilPenaltyMatter}
            />
          )}
          {step === 3 && (
            <ActsStep form={form} onChange={setTaxpayerField} />
          )}
          {step === 4 && (
            <>
              {canOfferPartnerCopy && jointPartner && (
                <label style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 18,
                  padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                  background: copyToSpouse ? 'rgba(122,162,255,.09)' : 'rgba(255,255,255,.02)',
                  border: `1px solid ${copyToSpouse ? 'rgba(122,162,255,.35)' : 'var(--border)'}`,
                  transition: 'all .15s',
                }}>
                  <input
                    type="checkbox"
                    checked={copyToSpouse}
                    onChange={(e) => setCopyToSpouse(e.target.checked)}
                    style={{ marginTop: 3, flexShrink: 0 }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      Also prepare a POA for {jointPartner.name}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3, lineHeight: 1.5 }}>
                      A joint return needs a separate Form 2848 per spouse. Creating this one will
                      open a second form pre-filled with the same representatives, tax matters,
                      and acts — you'll just review it and submit.
                    </div>
                  </div>
                </label>
              )}
              <ReviewStep
                form={form} submitting={submitting}
                onSubmit={handleSubmit} onBack={handleBack}
              />
            </>
          )}
        </div>

        {/* Navigation (hidden on review step — it has its own buttons) */}
        {!isLastStep && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <button className="btn" onClick={handleBack} disabled={step === 0 || prefillLoading} style={{ fontSize: 14, opacity: step === 0 || prefillLoading ? .4 : 1 }}>
              ← Back
            </button>
            <button
              className="btn"
              onClick={handleNext}
              disabled={prefillLoading || awaitingChoice}
              style={{ fontSize: 14, fontWeight: 600, background: 'rgba(122,162,255,.12)', borderColor: 'rgba(122,162,255,.4)', color: 'var(--accent)', opacity: prefillLoading || awaitingChoice ? .4 : 1 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
